import { useEffect, useMemo  } from "react";
import { TexType, getTexConfig, SpicesMap, type Cache } from "../../data/schema"; //prettier-ignore
import { useQueries } from "@tanstack/react-query";
import { useTextureManager } from "./useTextureManager";
import { LoadingStatus, useLoadingStore } from "@/features/loading/stores/useLoadingStore";
import { RESOURCES, type ResourceKey } from "@/data/resources";

// Helper to map TexType to resource key
export function getSpiceResourceKey(type: TexType): ResourceKey {
  switch (type) {
    case TexType.FALLBACK: return RESOURCES.spicesFallback.key;
    case TexType.TEXTURE: return RESOURCES.spicesTexture.key;
    case TexType.NORMAL: return RESOURCES.spicesNormal.key;
  }
}

export type useDataArrayTextureProps = {
  spices: SpicesMap;
  cache: Cache | undefined;
  type: TexType;
};

//prettier-ignore
export const useDataArrayTexture = ({ spices, cache, type, }: useDataArrayTextureProps) =>
{
  /** CPU Side - fetching data and converting it */
  const imgQueries = useQueries({
    queries: Array.from(spices).map(([id, v]) => ({
      queryKey: ["textures", type.toUpperCase(), id, v.name],
      queryFn: async () => {
        if (!cache) throw new Error("Cache not ready");
        const spice = await fetchTexture(spices, id, cache, type);
        const imageData = await extractImageDataBitmap(spice.image, type);
        return { spice, imageData };
      },
      staleTime: Infinity,
      enabled: Boolean(cache && id),
    })),
    combine: (results) => new Map(Array.from(spices).map(([id], i) => [id, results[i]] as const).filter(([, r]) => r.isEnabled)), //prettier-ignore
  });

  /**  GPU Side  (Tex manager) - uploading data to an actual gpu dataarraylayertexture */
  const { tex, layers, manager, status: layersStatus } = useTextureManager(type);
  // 1: Sync layer allocations with spices
  useEffect(() => {
    manager.sync(new Set(layers.keys()), new Set(spices.keys()));
  }, [spices, layers, manager]);

  // 2: Write to empty layers
  useEffect(() => {
    layers.forEach((layer, id) => {
      const query = imgQueries.get(id);
      // Write if: query succeeded, has data, and layer doesn't have data yet
      if (query?.isSuccess && query.data?.imageData && !layer.data) {
        manager.write(id, query.data.imageData);
      }
    });
  }, [spices, layers, imgQueries, manager]);

  /** Status 
   * Derivation of both the queries and the layer updates merged into one final status 
   * Aggregate queries + layers into one LoadingStatus 
   */
  const setStoreStatus = useLoadingStore((s) => s.setStatus);
  const status = useMemo(() => {
    // Queries
    const enabledQueries = [...imgQueries.values()];
    const queriesError = enabledQueries.some(q => q.isError);
    const queriesLoading = enabledQueries.some(q => q.isLoading);
    const queriesSuccess = enabledQueries.length > 0 && enabledQueries.every(q => q.isSuccess);

    // Layers
    const layerArray = [...layers.values()];
    const layersError = layersStatus === LoadingStatus.ERROR;
    const layersLoading = layersStatus === LoadingStatus.LOADING;
    const layersHaveAllData = layerArray.length > 0 && layerArray.every(l => l.hasSourceData);

    // Aggregate
    if (queriesError || layersError) return LoadingStatus.ERROR;
    if (queriesLoading || layersLoading) return LoadingStatus.LOADING;
    if (queriesSuccess && layersHaveAllData) return LoadingStatus.LOADED;
    return LoadingStatus.IDLE;
  }, [imgQueries, layers, layersStatus]);

  // Update global store
  useEffect(() => {
    setStoreStatus(getSpiceResourceKey(type), status);
  }, [status, type, setStoreStatus]);

  // Memoize return object to prevent cascading re-renders
  return useMemo(() => ({
    data: tex,
    layers,
    imgQueries, 
    status: status, 
    isError: status === LoadingStatus.ERROR,
    isLoading: status === LoadingStatus.LOADING,
    isSuccess: status === LoadingStatus.LOADED
  }), [tex, layers, status, imgQueries]);
};

/* Services */

// Fetch texture info from cache
export async function fetchTexture(
  spices: SpicesMap,
  spiceId: number,
  cache: Cache,
  type: TexType
) {
  try {
    const image = cache[spiceId as keyof typeof cache].textures[type];
    const name = spices.get(spiceId)?.name;
    const texSlot = (spices.get(spiceId) as { textureSlot?: number } | undefined)?.textureSlot;
    return { spiceId, image, name, texSlot };
  } catch (e) {
    throw new Error(
      `Failed to fetch ${type} texture for spice ${spiceId}: ${
        e instanceof Error ? e.message : e
      }`
    );
  }
}

// Extract image data to Uint8Array via canvas
export const extractImageDataBitmap = async (
  url: string,
  type: TexType
): Promise<Uint8Array> => {
  const config = getTexConfig(type);
  const response = await fetch(url);
  const bitmap = await createImageBitmap(await response.blob());

  const canvas = document.createElement("canvas");
  canvas.width = config.width;
  canvas.height = config.height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  ctx.drawImage(bitmap, 0, 0, config.width, config.height);

  bitmap.close();
  return new Uint8Array(
    ctx.getImageData(0, 0, config.width, config.height).data.buffer
  );
};
